import _ from 'lodash'
import memoizeOne from 'memoize-one'
import { utils, coreConstants } from '@adc/ui-morpheus'
import { isOnlineType, isTrusteeType } from '../util/common'
import { deserializeModeSelectDropdown } from '../util/venues'

const {
  date: {
    convertISOTimeToString,
    convertStringTimeToISO,
    formatDateApiToUi,
    formatDateApiToUiFromPattern,
    formatDateFromPattern,
    API_DATE_FORMAT,
    API_LOCAL_DATE_FORMAT,
  },
  common: { convertToApiFormat },
  formatters,
} = utils
const { MODES } = coreConstants.optionLists
const { UI_DATE_FORMAT } = coreConstants.statics

const apiToUiDataRowsTranslation = memoizeOne((dataRows, override = {}) => {
  if (!dataRows) return []
  const formatEndTime = (value, isOnline) => {
    if (!value) return isOnline ? '' : 'n/a'
    return formatDateApiToUiFromPattern(value, API_DATE_FORMAT)
  }
  return dataRows.map(row => {
    const newRow = _.cloneDeep(row)
    const isOnline = isOnlineType(row.venue_type)
    const address = utils.address.formatAddress(row.venue_address)

    if (newRow?.venue_schedule) {
      newRow.venue_schedule.initial_bidding_end_date_time = formatEndTime(
        row.venue_schedule.initial_bidding_end_date_time,
        isOnline
      )
    }

    if (row.venue_configuration) {
      newRow.venue_configuration.web_live_status = row.venue_configuration
        .web_live_status
        ? 'Active'
        : 'Inactive'
    }

    return {
      ...newRow,
      ...address,
      ...override,
      __computed__: {
        street_address: address.address,
      },
    }
  })
}, _.isEqual)

const apiToUiDataRowTranslation = row => apiToUiDataRowsTranslation([row])[0]

const populateVenuesFromVenueTemplates = (templates, override = {}) => {
  const venues = []
  _.each(templates, template => {
    // Populate venue for each schedule, including no schedules
    const schedules = _.isEmpty(template.schedules) ? [{}] : template.schedules
    _.each(schedules, schedule => {
      const venueType = _.get(template, 'venue_configuration.venue_type')
      const eventType = _.get(template, 'venue_configuration.event_types[0]')
      const trusteeSale = isTrusteeType(eventType) ? 'Yes' : 'No'
      const webLiveStatus = _.get(
        template,
        'venue_configuration.web_live_status',
        false
      )
      const holdingVenue = _.get(
        template,
        'venue_configuration.holding_venue',
        false
      )
      const isMinimumBidEnabled = _.get(
        template,
        'venue_configuration.is_minimum_bid_enabled',
        false
      )

      venues.push({
        ...template,
        venue_type: formatters.formatTitleCase(venueType),
        trustee_sale: !eventType ? '' : trusteeSale,
        web_live_status: webLiveStatus ? 'Active' : 'Inactive',
        holding_venue: holdingVenue ? 'Yes' : 'No',
        is_minimum_bid_enabled: isMinimumBidEnabled ? 'Yes' : 'No',
        venue_schedule: schedule,
        ...override,
      })
    })
  })

  return venues
}

const venueAddressFields = [
  'streetNumber',
  'streetName',
  'municipality',
  'countryPrimarySubdivision',
  'countrySecondarySubdivision',
  'postalCode',
  'streetUnit',
  'coordinates',
]

const venueConfigurationFields = [
  'auctioncomAuctioneer',
  'webLiveStatus',
  'venueClassification',
  'venueSetting',
  'trusteeProxyBidding',
  'estimatedAttendance',
  'holdingVenue',
  'schedulingStateCodes',
  'schedulingSellerCodes',
  'isMinimumBidEnabled',
]

const venueScheduleFields = [
  'auctionStartDate',
  'auctionEndDate',
  'registrationTime',
  'bidStartTime',
  'bidEndTime',
]

const baseVenueFields = [
  'eventId',
  'venueId',
  'venueCode',
  'venueName',
  'venueTitle',
  'venueLocationDirections',
  'venueType',
  'venueUrl',
  'trusteeSale',
]

// TODO: remove this once BE removes deprecated fields and moves to local times permanently
const excludedFields = [
  'bid_start_time',
  'bid_start_date_time',
  'initial_bidding_end_time',
  'initial_bidding_end_date_time',
]

const getUiValues = (apiKeys, apiValues) =>
  apiKeys
    .filter(key => !excludedFields.includes(key))
    .reduce((uiValues, apiKey) => {
      const uiKey = _.camelCase(apiKey)
      const uiValue = apiValues[apiKey]
      let result = {}

      if (uiKey === 'bidStartLocalDate') {
        const auctionStartDate = formatDateFromPattern(
          uiValue,
          API_LOCAL_DATE_FORMAT,
          UI_DATE_FORMAT
        )
        result = {
          ...uiValues,
          auctionStartDate,
        }
      } else if (uiKey === 'bidStartLocalTime') {
        const bidStartTime = convertISOTimeToString(uiValue)
        result = {
          ...uiValues,
          bidStartTime,
        }
      } else if (uiKey === 'initialBiddingEndLocalDate') {
        const auctionEndDate = formatDateFromPattern(
          uiValue,
          API_LOCAL_DATE_FORMAT,
          UI_DATE_FORMAT
        )
        result = {
          ...uiValues,
          auctionEndDate,
        }
      } else if (uiKey === 'initialBiddingEndLocalTime') {
        const bidEndTime = convertISOTimeToString(uiValue)
        result = {
          ...uiValues,
          bidEndTime,
        }
      } else {
        result = {
          ...uiValues,
          [uiKey]: uiValue,
        }
      }

      return result
    }, {})

const apiToUi = values => {
  const {
    venue_configuration: config,
    venue_schedule: schedule,
    venue_address: address,
    ...restApiValues
  } = values

  const apiValues = {
    ...config,
    ...schedule,
    ...address,
    ...restApiValues,
  }

  const apiKeys = _.keys(apiValues)
  const uiValues = getUiValues(apiKeys, apiValues)
  const schedulingStateCodes = {
    mode: (uiValues.schedulingStateCodes || []).length
      ? MODES.INCLUDE
      : MODES.ALL,
    value: uiValues.schedulingStateCodes || [],
  }
  const schedulingSellerCodes = {
    mode: (uiValues.schedulingSellerCodes || []).length
      ? MODES.INCLUDE
      : MODES.ALL,
    value: uiValues.schedulingSellerCodes || [],
  }

  uiValues.venueAddressSuggestion = {
    ...address,
    selectedAddressText: _.trim(
      `${uiValues.streetNumber || ''} ${uiValues.streetName || ''}`
    ),
  }

  uiValues.eventCode =
    uiValues.event && (uiValues.event.event_code || uiValues.event.event_id)
  uiValues.eventType = uiValues.event && uiValues.event.event_type
  uiValues.comingSoon =
    uiValues.event && uiValues.event.event_configuration.coming_soon
  uiValues.campaignStartDate =
    uiValues.event &&
    formatDateApiToUi(
      uiValues.event.event_schedule.campaign_start_date,
      UI_DATE_FORMAT
    )
  uiValues.schedulingStateCodes = JSON.stringify(schedulingStateCodes)
  uiValues.schedulingSellerCodes = JSON.stringify(schedulingSellerCodes)

  return uiValues
}

const getBaseVenueFields = (fields, values) => {
  const apiValues = convertToApiFormat(fields, values)

  // handle special cases
  if (
    apiValues.venue_url &&
    /^(http:\/\/|https:\/\/)$/.test(apiValues.venue_url)
  ) {
    delete apiValues.venue_url
  }

  // add default status as active
  apiValues.status = 'ACTIVE'

  return apiValues
}

const getVenueSchedule = (fields, values) => {
  const apiValues = convertToApiFormat(fields, values)

  // handle special cases
  apiValues.bid_start_local_date = formatDateFromPattern(
    apiValues.auction_start_date,
    UI_DATE_FORMAT,
    API_LOCAL_DATE_FORMAT
  )

  apiValues.bid_start_local_time = convertStringTimeToISO(
    apiValues.bid_start_time
  )

  apiValues.initial_bidding_end_local_date = formatDateFromPattern(
    apiValues.auction_end_date,
    UI_DATE_FORMAT,
    API_LOCAL_DATE_FORMAT
  )

  apiValues.initial_bidding_end_local_time = convertStringTimeToISO(
    apiValues.bid_end_time
  )

  delete apiValues.bid_start_date_time
  delete apiValues.initial_bidding_end_date_time
  delete apiValues.bid_start_time
  delete apiValues.bid_end_time

  return apiValues
}

const getVenueAddress = (fields, values) => {
  let apiValues = convertToApiFormat(fields, values)

  // handle special cases
  if (values.venueAddressSuggestion) {
    if (values.venueAddressSuggestion.street_number) {
      apiValues.street_number = values.venueAddressSuggestion.street_number
    }

    if (values.venueAddressSuggestion.street_name) {
      apiValues.street_name = values.venueAddressSuggestion.street_name
    }
  }

  /**
   * If no address is passed in the form
   * apivalues will have all keys with undefined.
   * return null to match api needs
   */
  if (_.some(apiValues, value => !_.isUndefined(value))) {
    // enforce default country code if apiValues are not empty
    apiValues.country_code = 'US'
  } else {
    apiValues = null
  }

  return apiValues
}

const uiToApi = values => {
  const baseVenue = getBaseVenueFields(baseVenueFields, values)
  const venueConfiguration = convertToApiFormat(
    venueConfigurationFields,
    values
  )
  const venueSchedule = getVenueSchedule(venueScheduleFields, values)
  const venueAddress = getVenueAddress(venueAddressFields, values)
  const schedulingStateCodes = venueConfiguration.scheduling_state_codes
  venueConfiguration.scheduling_state_codes =
    deserializeModeSelectDropdown(schedulingStateCodes)
  const schedulingSellerCodes = venueConfiguration.scheduling_seller_codes
  venueConfiguration.scheduling_seller_codes = deserializeModeSelectDropdown(
    schedulingSellerCodes
  )

  const result = {
    ...baseVenue,
    venue_configuration: venueConfiguration,
    venue_schedule: venueSchedule,
    venue_address: venueAddress,
  }

  return result
}

// @TODO: remove this and use just the uiToApi function. Currently it returns more
// information than we want, when the bulk upload fields grow, it should be perfect.
const uiToApiBulkFields = values => {
  return {
    ...getBaseVenueFields(baseVenueFields, values),
    venue_configuration: convertToApiFormat(venueConfigurationFields, values),
    venue_schedule: getVenueSchedule(venueScheduleFields, values),
  }
}

export {
  apiToUiDataRowsTranslation,
  apiToUiDataRowTranslation,
  populateVenuesFromVenueTemplates,
  uiToApiBulkFields,
  uiToApi,
  apiToUi,
}
